import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioEsimRegionObject from '@/entities/ratioEsimRegion/interface'

import {
  ALL_ESIM_CONFIGS_QUERY,
  SINGLE_ESIM_CONFIG_QUERY,
  ESIM_CONFIGS_DEPENDENTS_QUERY,
  CREATE_ESIM_CONFIG_MUTATION,
  UPDATE_ESIM_CONFIG_MUTATION,
  DELETE_ESIM_CONFIG_MUTATION,
  FORCE_DELETE_ESIM_CONFIG_MUTATION,
  ADD_ESIM_CONFIG_REGIONS_MUTATION,
  REMOVE_ESIM_CONFIG_REGIONS_MUTATION,
  CREATE_ESIM_CONFIG_REGIONS_MUTATION,
  DELETE_ESIM_CONFIG_REGIONS_MUTATION,
  UPDATE_ESIM_CONFIG_REGIONS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioEsimConfigRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_ESIM_CONFIG_QUERY, variables).then((data: any) => {
      return hydrateObject(data.esimConfig)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_ESIM_CONFIGS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.esimConfigs)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_ESIM_CONFIG_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.esimConfig),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_ESIM_CONFIGS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.esimConfigs),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(ESIM_CONFIGS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([ESIM_CONFIGS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_ESIM_CONFIG_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.esimConfig)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_ESIM_CONFIG_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.esimConfig)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_ESIM_CONFIG_MUTATION, variables).then((data: any) => {
      return data.deleteRatioEsimConfig
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_ESIM_CONFIG_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioEsimConfig
    })
  },

  assignRegions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        regionIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_ESIM_CONFIG_REGIONS_MUTATION, variables)
  },

  unassignRegions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        regionIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_ESIM_CONFIG_REGIONS_MUTATION, variables)
  },

  createRegions: async (id: string, items: Array<ratioEsimRegionObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        regions: items,
      },
    }
    return graphQLClient.request(CREATE_ESIM_CONFIG_REGIONS_MUTATION, variables)
  },

  deleteRegions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        regionIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_ESIM_CONFIG_REGIONS_MUTATION, variables)
  },

  updateRegionsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        regionIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_ESIM_CONFIG_REGIONS_ORDER_MUTATION, variables)
  },
}

export default ratioEsimConfigRepository
